import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useActiveStudio } from '@/state/useActiveStudio';
import { isAllStudios, ALL_STUDIOS_SCOPE, activeStudioIds, studioOptions } from '@/domain/studioScope';
import type {
  Artist,
  ArtistAvailability,
  ArtistRates,
  DayOfWeek,
  DesignType,
  LeaveBlock,
  StudioId,
} from '@/domain/types';
import {
  addLeaveBlock,
  removeLeaveBlock,
  upcomingLeave,
  formatLeaveRange,
  createArtist,
  slugifyArtistId,
  leaveBlockKind,
} from '@/domain/artistAdmin';
import { defaultArtistDeposits } from '@/domain/pricing';
import { persistArtist } from '@/api/persist';
import { dayOffTileTone } from '@/utils/statusTone';
import {
  leaveTallyByType,
  allowanceUsedThisYear,
  LEAVE_TYPES,
  LEAVE_TYPE_LABEL,
  ALLOWANCE_CONSUMING_TYPES,
} from '@/domain/leaveTally';
import { countDoubleDaysSinceLastDayOff } from '@/domain/workload';
import DoubleDayBadge from '@/components/DoubleDayBadge';
import AddArtistModal from '@/screens/artists/AddArtistModal';
import CommissionEditor from '@/screens/artists/CommissionEditor';
import { COMMISSION_EDITORS, describeArrangement } from '@atink/domain';
import { arrangementForArtist } from '@/domain/commissionEngine';
import { bookingsDuringLeave, leaveCapMessage, saveArtistLeave } from '@/domain/artistFlow';
import { studioDayStartIso } from '@atink/domain';
import { formatStudioDay } from '@/utils/studioDay';
import { currentActorId, actorLabel } from '@/domain/actor';
import clsx from 'clsx';
import { format } from 'date-fns';
import { CalendarDays, ChevronDown, ChevronRight, DollarSign, Mail, Plus, X } from 'lucide-react';

const DESIGN_TYPES: DesignType[] = [
  'black-and-grey',
  'colour-realism',
  'traditional',
  'neo-traditional',
  'fine-line',
  'lettering',
  'cover-up',
  'blackwork',
  'sleeve',
  'half-sleeve',
  'chest-panel',
  'back-piece',
  'flash',
];

const DAYS: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL: Record<DayOfWeek, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

function toggleDay(availability: ArtistAvailability, day: DayOfWeek): ArtistAvailability {
  const current = availability.pattern[day];
  return {
    ...availability,
    pattern: {
      ...availability.pattern,
      [day]: current ? null : { startTime: '10:00', endTime: '19:00' },
    },
  };
}

export default function ArtistManagement() {
  const artistsById = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const bookings = useBrainStore((s) => s.bookings);
  const upsertArtist = useBrainStore((s) => s.upsertArtist);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const persona = useSessionStore((s) => s.persona);
  const clockIso = useSimulationStore((s) => s.clockIso);
  // Studio scope now follows the top-bar switcher (single source of truth) instead
  // of a local dropdown. The assignedStudios/allowAllStudios gate below is kept.
  const { activeStudio } = useActiveStudio();
  const setActiveStudio = useSessionStore((s) => s.setActiveStudio);

  const allowAllStudios = persona === 'georgina' || persona === 'owner';
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  // L7: swap the roster+detail view for the per-studio leave ledger.
  const [showLedger, setShowLedger] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [leaveStart, setLeaveStart] = useState('');
  const [leaveEnd, setLeaveEnd] = useState('');
  const [leaveType, setLeaveType] = useState<LeaveBlock['type']>('time-off');
  const [leaveNote, setLeaveNote] = useState('');
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const [pastOpen, setPastOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const savedTimer = useRef<number | null>(null);
  const flashSaved = () => {
    setJustSaved(true);
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setJustSaved(false), 1500);
  };
  useEffect(() => {
    return () => {
      if (savedTimer.current) window.clearTimeout(savedTimer.current);
    };
  }, []);
  const save = (a: Artist) => {
    upsertArtist(a); // optimistic in-memory update
    persistArtist(a); // dedicated write to app.artists so the edit survives reload
    flashSaved();
  };

  // Commission is Owner/Ops-only and server-checked (B9 / 75nk): optimistic, then
  // reverted with the server's reason if the write is refused.
  const canEditCommission = COMMISSION_EDITORS.has(persona ?? '');
  const saveCommission = (next: Artist) => {
    const prev = artistsById[next.id];
    if (!prev) return;
    upsertArtist(next);
    // `commissionChange` marks this as an explicit commission save: the server
    // ignores commission fields on every other artist write.
    void persistArtist({ ...next, commissionChange: true }, {
      onReject: (_status, body) => {
        // Put back only the commission, onto whatever the artist is now (a day off
        // saved meanwhile must survive the revert).
        const cur = useBrainStore.getState().artists[next.id] ?? prev;
        const { commissionArrangement: _dropped, ...rest } = cur;
        upsertArtist({
          ...rest,
          commissionRate: prev.commissionRate,
          ...(prev.commissionArrangement ? { commissionArrangement: prev.commissionArrangement } : {}),
        });
        pushNotification({
          intensity: 'critical',
          title: "Couldn't change the commission",
          body: body?.message ?? 'The server refused the change, so the old commission stands.',
          ttlMs: 6000,
        });
      },
    }).then((outcome) => {
      if (outcome !== 'ok') return;
      flashSaved();
      useBrainStore.getState().appendActivity({
        id: `act-commission-${next.id}-${Date.parse(clockIso)}`,
        type: 'commission-changed',
        studioId: next.studioId,
        artistId: next.id,
        body: `Commission for ${next.displayName} changed from ${describeArrangement(arrangementForArtist(prev))} to ${describeArrangement(
          arrangementForArtist(next),
        )} by ${actorLabel(currentActorId() ?? persona ?? 'ops')}. Applies to days not yet paid.`,
        timestamp: clockIso,
      });
    });
  };

  const artistList = useMemo(() => {
    const allowed = allowAllStudios
      ? null
      : new Set<StudioId>(assignedStudios);
    return Object.values(artistsById)
      .filter((a) => {
        if (allowed && !allowed.has(a.studioId)) return false;
        if (!isAllStudios(activeStudio) && a.studioId !== activeStudio) return false;
        // Archived view shows ONLY archived artists; default view shows only active ones.
        if (showArchived ? !a.archived : a.archived) return false;
        return true;
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [artistsById, activeStudio, allowAllStudios, assignedStudios, showArchived]);

  const grouped = useMemo(() => {
    const map = new Map<string, Artist[]>();
    for (const a of artistList) {
      if (!map.has(a.studioId)) map.set(a.studioId, []);
      map.get(a.studioId)!.push(a);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [artistList]);

  const selected = selectedId ? artistsById[selectedId] : undefined;

  const [emailDraft, setEmailDraft] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [firstNameDraft, setFirstNameDraft] = useState('');
  const [lastNameDraft, setLastNameDraft] = useState('');
  const [displayNameDraft, setDisplayNameDraft] = useState('');
  useEffect(() => {
    setEmailDraft(selected?.email ?? '');
    setPhoneDraft(selected?.phone ?? '');
    setFirstNameDraft(selected?.firstName ?? '');
    setLastNameDraft(selected?.lastName ?? '');
    setDisplayNameDraft(selected?.displayName ?? '');
    setConfirmingArchive(false);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCommitEmail = () => {
    if (selected && emailDraft !== (selected.email ?? '')) {
      save({ ...selected, email: emailDraft || undefined });
    }
  };
  const onCommitPhone = () => {
    if (selected && phoneDraft !== (selected.phone ?? '')) {
      save({ ...selected, phone: phoneDraft || undefined });
    }
  };
  const onCommitFirstName = () => {
    const next = firstNameDraft.trim();
    if (selected && next && next !== selected.firstName) {
      save({ ...selected, firstName: next });
    }
  };
  const onCommitLastName = () => {
    const next = lastNameDraft.trim();
    if (selected && next && next !== selected.lastName) {
      save({ ...selected, lastName: next });
    }
  };
  const onCommitDisplayName = () => {
    const next = displayNameDraft.trim();
    if (selected && next && next !== selected.displayName) {
      save({ ...selected, displayName: next });
    }
  };

  const onToggleDay = (day: DayOfWeek) => {
    if (!selected) return;
    save({ ...selected, availability: toggleDay(selected.availability, day) });
  };

  const onUpdateHours = (day: DayOfWeek, field: 'startTime' | 'endTime', value: string) => {
    if (!selected) return;
    const current = selected.availability.pattern[day];
    if (!current) return;
    save({
      ...selected,
      availability: {
        ...selected.availability,
        pattern: {
          ...selected.availability.pattern,
          [day]: { ...current, [field]: value },
        },
      },
    });
  };

  const onChangeStudio = (studioId: StudioId) => {
    if (!selected) return;
    save({ ...selected, studioId });
    pushNotification({
      intensity: 'info',
      title: 'Studio updated',
      body: `${selected.displayName} now rostered at ${studios[studioId]?.name ?? studioId}.`,
      ttlMs: 3500,
    });
  };

  const onToggleSpeciality = (type: DesignType) => {
    if (!selected) return;
    const has = selected.styleSpeciality.includes(type);
    const next = has
      ? selected.styleSpeciality.filter((t) => t !== type)
      : [...selected.styleSpeciality, type];
    save({ ...selected, styleSpeciality: next });
  };

  const onUpdateRate = (field: keyof ArtistRates, value: number) => {
    if (!selected) return;
    save({
      ...selected,
      rates: { ...selected.rates, [field]: Math.max(0, Math.round(value)) },
    });
  };

  // L7: annual leave allowance is owner/georgina-gated (same as `allowAllStudios`).
  const onUpdateAllowance = (value: number) => {
    if (!selected || !allowAllStudios) return;
    save({ ...selected, leaveAllowanceDays: Math.max(0, Math.round(value)) });
  };

  const onAddLeave = async () => {
    if (!selected || !leaveStart) return;
    const endIsoDate = leaveEnd || leaveStart;
    // D10-02: studio (Melbourne) days, whatever the browser's zone.
    const newBlock: LeaveBlock = {
      id: crypto.randomUUID(),
      startDate: studioDayStartIso(leaveStart),
      endDate: studioDayStartIso(endIsoDate),
      type: leaveType,
      note: leaveNote || undefined,
    };
    // D10-02: capped by the annual allowance (working days; RDO/comp free).
    const overCap = leaveCapMessage(selected, newBlock);
    if (overCap) {
      setLeaveError(overCap);
      return;
    }
    setLeaveError(null);
    // Success is only said once the server has saved it (a refusal reverts and
    // says why instead).
    if (!(await saveArtistLeave({ ...selected, availability: addLeaveBlock(selected.availability, newBlock) }, selected))) return;
    flashSaved();
    // D10-02: leave never cancels anything, so say which bookings now need moving.
    const clashes = bookingsDuringLeave(selected.id, newBlock, bookings);
    pushNotification(
      clashes.length > 0
        ? {
            intensity: 'high',
            title: 'Leave added · bookings to move',
            body: `${selected.displayName} on leave ${formatLeaveRange(newBlock)}. ${clashes.length} booking${clashes.length === 1 ? ' is' : 's are'} on those days (${clashes
              .map((b) => formatStudioDay(b.start, 'EEE d MMM'))
              .join(', ')}); move ${clashes.length === 1 ? 'it' : 'them'} to another day or artist.`,
            ttlMs: 10000,
          }
        : {
            intensity: 'info',
            title: 'Leave added',
            body: `${selected.displayName} on leave ${formatLeaveRange(newBlock)}.`,
            ttlMs: 3500,
          },
    );
    setLeaveStart('');
    setLeaveEnd('');
    setLeaveNote('');
    setLeaveType('time-off');
  };

  const onRemoveLeave = (id: string) => {
    if (!selected) return;
    save({ ...selected, availability: removeLeaveBlock(selected.availability, id) });
  };

  const onArchive = () => {
    if (!selected) return;
    save({ ...selected, archived: true });
    setConfirmingArchive(false);
    pushNotification({
      intensity: 'info',
      title: 'Artist archived',
      body: `${selected.displayName} removed from the active roster.`,
      ttlMs: 3500,
    });
  };
  const onRestoreArtist = (artist: Artist) => {
    save({ ...artist, archived: false });
    pushNotification({
      intensity: 'info',
      title: 'Artist restored',
      body: `${artist.displayName} returned to the active roster.`,
      ttlMs: 3500,
    });
  };
  const onRestore = () => {
    if (selected) onRestoreArtist(selected);
  };

  const onCreateArtist = (input: { firstName: string; lastName: string; studioId: StudioId }) => {
    const id = slugifyArtistId(`${input.firstName} ${input.lastName}`, new Set(Object.keys(artistsById)));
    const artist = createArtist(input, id, clockIso);
    upsertArtist(artist);
    // Adding an artist sets a commission, so the server allows it to Owner/Ops
    // only; a refusal takes the new row back out of the list.
    void persistArtist(artist, {
      onReject: (_status, body) => {
        const { [id]: _gone, ...rest } = useBrainStore.getState().artists;
        useBrainStore.setState({ artists: rest });
        setSelectedId(null);
        pushNotification({
          intensity: 'critical',
          title: "Couldn't add the artist",
          body: body?.message ?? 'The server refused it.',
          ttlMs: 6000,
        });
      },
    });
    flashSaved();
    setShowAdd(false);
    setSelectedId(id);
    pushNotification({
      intensity: 'info',
      title: 'Artist added',
      body: `${artist.displayName} added to ${studios[artist.studioId]?.name ?? artist.studioId}.`,
      ttlMs: 3500,
    });
  };

  const upcomingLeaveFor = (a: Artist) => upcomingLeave(a.availability, clockIso);
  const leaveInvalid = !leaveStart || (leaveEnd !== '' && leaveEnd < leaveStart);

  return (
    <section className="px-8 py-8" data-testid="artist-management">
      <header className="flex items-end justify-between max-w-7xl flex-wrap gap-4">
        <div>
          <p className="label-small-caps text-gold-bright">Team</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Artists</h1>
          <p className="font-serif text-cream-soft mt-3 max-w-2xl">
            Working pattern + leave blocks for every artist. Toggle weekdays to set their
            standard roster. Add date ranges as leave when they need cover.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowLedger((v) => !v)}
            className="btn-quiet text-xs"
            data-testid="leave-ledger-toggle"
            aria-pressed={showLedger}
          >
            <CalendarDays size={12} /> {showLedger ? 'Back to roster' : 'Leave ledger'}
          </button>
          {canEditCommission ? (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="btn-primary text-xs"
              data-testid="artist-add"
            >
              <Plus size={12} /> Add artist
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="btn-quiet text-xs"
            data-testid="artist-show-archived"
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
          <label className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
            Studio
          </label>
          <select
            value={activeStudio ?? ALL_STUDIOS_SCOPE}
            onChange={(e) => setActiveStudio(e.target.value as StudioId | 'all')}
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
            data-testid="artist-studio-filter"
          >
            {assignedStudios.length > 1 ? (
              <option value={ALL_STUDIOS_SCOPE}>All studios</option>
            ) : null}
            {/* branding-3: archived studios drop out (the current pick stays). */}
            {studioOptions(studios, assignedStudios, activeStudio)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.brand} · {s.name}
                </option>
              ))}
          </select>
        </div>
      </header>

      {showLedger ? (
        <LeaveLedgerView
          groups={grouped}
          studioName={(id) => studios[id]?.name ?? id.replace('-', ' ')}
          nowIso={clockIso}
        />
      ) : (
      <div className="mt-6 grid grid-cols-12 gap-6 max-w-7xl">
        <section className="col-span-12 lg:col-span-7">
          <div className="panel p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="artist-table">
              <thead>
                <tr className="text-left text-cream-faint font-inter text-[10px] uppercase tracking-widest border-b border-gold-dim/20">
                  <th className="px-4 py-2.5 font-normal">Artist</th>
                  <th className="px-2 py-2.5 font-normal">Speciality</th>
                  {DAYS.map((d) => (
                    <th key={d} className="px-1 py-2.5 font-normal text-center w-7">
                      {DAY_LABEL[d][0]}
                    </th>
                  ))}
                  <th className="px-3 py-2.5 font-normal text-right">Off</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(([studioId, list]) => (
                  <Fragment key={studioId}>
                    <tr data-testid={`artist-studio-${studioId}`}>
                      <td
                        colSpan={DAYS.length + 3}
                        className="px-4 pt-4 pb-1 label-small-caps text-gold-bright bg-ink-deep"
                      >
                        {studios[studioId]?.name ?? studioId.replace('-', ' ')} · {list.length} artists
                      </td>
                    </tr>
                    {list.map((artist) => {
                      const offCount = upcomingLeaveFor(artist).length;
                      const active = selectedId === artist.id;
                      return (
                        <tr
                          key={artist.id}
                          onClick={() => setSelectedId(artist.id)}
                          className={clsx(
                            'cursor-pointer border-t border-gold-dim/10 transition',
                            active
                              ? 'bg-gold/10'
                              : 'hover:bg-ink-soft/50',
                          )}
                          data-testid={`artist-row-${artist.id}`}
                        >
                          <td className="px-4 py-2 font-inter text-cream whitespace-nowrap">
                            {artist.displayName}
                            {artist.isApprentice ? (
                              <span className="ml-2 font-inter text-[9px] text-gold-dim uppercase tracking-widest">
                                apprentice
                              </span>
                            ) : null}
                          </td>
                          <td className="px-2 py-2 font-inter text-[10px] text-cream-soft uppercase tracking-widest truncate max-w-[200px]">
                            {artist.styleSpeciality.join(', ').replace(/-/g, ' ')}
                          </td>
                          {DAYS.map((d) => {
                            const on = !!artist.availability.pattern[d];
                            return (
                              <td key={d} className="px-1 py-2 text-center">
                                <span
                                  title={`${DAY_LABEL[d]} · ${on ? 'working' : 'off'}`}
                                  className={clsx(
                                    'inline-block w-5 h-5 rounded-sm text-[10px] font-mono leading-5',
                                    on
                                      ? 'bg-mint/45 text-ink-black'
                                      : 'bg-blood/35 text-blood',
                                  )}
                                >
                                  {DAY_LABEL[d][0]}
                                </span>
                              </td>
                            );
                          })}
                          <td className="px-3 py-2 font-inter text-[10px] uppercase tracking-widest text-right">
                            {artist.archived ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRestoreArtist(artist);
                                }}
                                className="btn-quiet text-[10px]"
                                data-testid={`artist-row-restore-${artist.id}`}
                              >
                                Restore
                              </button>
                            ) : offCount > 0 ? (
                              <span className="text-gold-bright">{offCount}</span>
                            ) : (
                              <span className="text-cream-faint">·</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
                {grouped.length === 0 ? (
                  <tr>
                    <td
                      colSpan={DAYS.length + 3}
                      className="px-4 py-8 text-center font-serif text-cream-soft"
                      data-testid="artist-table-empty"
                    >
                      {showArchived
                        ? 'No archived artists. Toggle off to see the active roster.'
                        : 'No artists in this view.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="col-span-12 lg:col-span-5 lg:sticky lg:top-4 self-start">
          {selected ? (
            <div className="panel p-5 space-y-5" data-testid="artist-detail">
              <header className="flex items-start justify-between gap-3">
                <div>
                  <p className="label-small-caps text-gold-bright">Editing</p>
                  <h2 className="heading-display text-2xl text-cream mt-1">
                    {selected.displayName}
                  </h2>
                  <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                    {studios[selected.studioId]?.name ?? selected.studioId}
                  </p>
                  <p
                    className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-1"
                    data-testid="artist-id"
                  >
                    ID · {selected.id}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {justSaved ? (
                    <span
                      className="font-inter text-[10px] text-mint uppercase tracking-widest"
                      data-testid="artist-saved-indicator"
                    >
                      Saved ✓
                    </span>
                  ) : (
                    <span className="font-inter text-[10px] text-cream-faint uppercase tracking-widest">
                      Auto-saved
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedId(null)}
                    className="btn-quiet"
                    aria-label="Close"
                    data-testid="artist-detail-close"
                  >
                    <X size={14} />
                  </button>
                </div>
              </header>

              <section>
                <p className="label-small-caps mb-2">Name</p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                      First name
                    </span>
                    <input
                      type="text"
                      value={firstNameDraft}
                      onChange={(e) => setFirstNameDraft(e.target.value)}
                      onBlur={onCommitFirstName}
                      className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
                      data-testid="artist-first-name"
                    />
                  </label>
                  <label className="block">
                    <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                      Last name
                    </span>
                    <input
                      type="text"
                      value={lastNameDraft}
                      onChange={(e) => setLastNameDraft(e.target.value)}
                      onBlur={onCommitLastName}
                      className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
                      data-testid="artist-last-name"
                    />
                  </label>
                  <label className="block col-span-2">
                    <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                      Display name
                    </span>
                    <input
                      type="text"
                      value={displayNameDraft}
                      onChange={(e) => setDisplayNameDraft(e.target.value)}
                      onBlur={onCommitDisplayName}
                      className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
                      data-testid="artist-display-name"
                    />
                  </label>
                </div>
                <p className="font-serif text-[11px] text-cream-soft mt-2">
                  Display name is what shows on the roster, calendar, and customer records. Saved when you click away.
                </p>
              </section>

              <section>
                <p className="label-small-caps mb-2 flex items-center gap-1.5">
                  <Mail size={11} /> Contact
                </p>
                <div className="grid grid-cols-1 gap-2">
                  <label className="block">
                    <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                      Email
                    </span>
                    <input
                      type="email"
                      value={emailDraft}
                      onChange={(e) => setEmailDraft(e.target.value)}
                      onBlur={onCommitEmail}
                      placeholder="name@addiktedtoink.com.au"
                      className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
                      data-testid="artist-email"
                    />
                  </label>
                  <label className="block">
                    <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                      Phone
                    </span>
                    <input
                      type="tel"
                      value={phoneDraft}
                      onChange={(e) => setPhoneDraft(e.target.value)}
                      onBlur={onCommitPhone}
                      placeholder="0412 345 678"
                      className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
                      data-testid="artist-phone"
                    />
                  </label>
                </div>
                <p className="font-serif text-[11px] text-cream-soft mt-2">
                  Saved when you click away. Used for booking confirmations and reminders.
                </p>
              </section>

              <section>
                <p className="label-small-caps mb-2">Studio</p>
                <select
                  value={selected.studioId}
                  onChange={(e) => onChangeStudio(e.target.value as StudioId)}
                  className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
                  data-testid="artist-studio"
                >
                  {/* branding-3: an archived studio isn't offered, but the artist's own stays. */}
                  {studioOptions(studios, undefined, selected.studioId).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.brand} · {s.name}
                    </option>
                  ))}
                </select>
              </section>

              <section>
                <p className="label-small-caps mb-2">Speciality</p>
                <div className="flex flex-wrap gap-1.5" data-testid="artist-specialities">
                  {DESIGN_TYPES.map((type) => {
                    const active = selected.styleSpeciality.includes(type);
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => onToggleSpeciality(type)}
                        className={clsx(
                          'px-2.5 py-1 rounded-full border text-[11px] font-inter uppercase tracking-widest transition',
                          active
                            ? 'border-gold-bright bg-gold/15 text-gold-bright'
                            : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                        )}
                        data-testid={`artist-speciality-${type}`}
                      >
                        {type.replace(/-/g, ' ')}
                      </button>
                    );
                  })}
                </div>
              </section>

              <section>
                <p className="label-small-caps mb-2 flex items-center gap-1.5">
                  <DollarSign size={11} /> Rates
                </p>
                <div className="grid grid-cols-2 gap-2" data-testid="artist-rates">
                  {([
                    ['hourlyAud', 'Hourly'],
                    ['smallAud', 'Small (≤2h)'],
                    ['halfDayAud', 'Half day'],
                    ['fullDayAud', 'Full day'],
                  ] as Array<[keyof ArtistRates, string]>).map(([key, label]) => (
                    <label
                      key={key}
                      className="block"
                      data-testid={`artist-rate-${key}`}
                    >
                      <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                        {label}
                      </span>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-xs text-cream-faint">
                          $
                        </span>
                        <input
                          type="number"
                          min={0}
                          step={10}
                          value={selected.rates[key]}
                          onChange={(e) => onUpdateRate(key, parseInt(e.target.value, 10) || 0)}
                          className="w-full bg-ink-deep border border-gold-dim/40 rounded-md pl-6 pr-3 py-1.5 text-sm text-cream font-inter"
                        />
                      </div>
                    </label>
                  ))}
                </div>
                <p className="font-serif text-[11px] text-cream-soft mt-2">
                  Used to bill bookings. Updates immediately flow through to the calendar and
                  customer records.
                </p>
              </section>

              <CommissionEditor
                key={`${selected.id}-${JSON.stringify(arrangementForArtist(selected))}`}
                artist={selected}
                canEdit={canEditCommission}
                onSave={saveCommission}
              />

              <section>
                <p className="label-small-caps mb-2 flex items-center gap-1.5">
                  <DollarSign size={11} /> Deposits
                </p>
                <div className="grid grid-cols-2 gap-2" data-testid="artist-deposits">
                  {([
                    ['depositSmallAud', 'Small'],
                    ['depositHalfDayAud', 'Half day'],
                    ['depositFullDayAud', 'Full day'],
                    ['depositMultiSessionAud', 'Multi-session'],
                  ] as Array<[keyof ReturnType<typeof defaultArtistDeposits>, string]>).map(
                    ([key, label]) => (
                      <label key={key} className="block">
                        <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                          {label}
                        </span>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-xs text-cream-faint">
                            $
                          </span>
                          <input
                            type="number"
                            min={0}
                            step={10}
                            value={selected.rates[key] ?? defaultArtistDeposits(selected.rates)[key]}
                            onChange={(e) => onUpdateRate(key, parseInt(e.target.value, 10) || 0)}
                            data-testid={`artist-deposit-${key}`}
                            className="w-full bg-ink-deep border border-gold-dim/40 rounded-md pl-6 pr-3 py-1.5 text-sm text-cream font-inter"
                          />
                        </div>
                      </label>
                    ),
                  )}
                </div>
                <p className="font-serif text-[11px] text-cream-soft mt-2">
                  Taken up front per session tier. Prefilled at 50% of the day rate; edit to set
                  this artist&rsquo;s own amounts. New booking proposals use these.
                </p>
              </section>

              <section>
                <p className="label-small-caps mb-2">Weekly pattern</p>
                <div className="space-y-1.5">
                  {DAYS.map((d) => {
                    const window = selected.availability.pattern[d];
                    const on = !!window;
                    return (
                      <div
                        key={d}
                        className={clsx(
                          'flex items-center gap-3 px-3 py-2 rounded-md border',
                          on
                            ? 'border-mint/40 bg-mint/5'
                            : 'border-blood/30 bg-blood/5 opacity-80',
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => onToggleDay(d)}
                          className={clsx(
                            'w-10 h-7 rounded-sm font-inter text-[10px] uppercase tracking-widest transition shrink-0',
                            on ? 'bg-mint/45 text-ink-black' : 'bg-blood/30 text-blood',
                          )}
                          data-testid={`artist-day-${d}`}
                        >
                          {DAY_LABEL[d]}
                        </button>
                        {on ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="time"
                              value={window!.startTime}
                              onChange={(e) => onUpdateHours(d, 'startTime', e.target.value)}
                              className="bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1 text-xs text-cream font-mono"
                              data-testid={`artist-day-${d}-start`}
                            />
                            <span className="font-inter text-[10px] text-cream-faint uppercase tracking-widest">
                              to
                            </span>
                            <input
                              type="time"
                              value={window!.endTime}
                              onChange={(e) => onUpdateHours(d, 'endTime', e.target.value)}
                              className="bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1 text-xs text-cream font-mono"
                              data-testid={`artist-day-${d}-end`}
                            />
                          </div>
                        ) : (
                          <span className="flex-1 font-serif text-sm text-cream-soft">
                            Off
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="font-serif text-[11px] text-cream-soft mt-2">
                  Click the day pill to toggle on/off. Hours can vary day to day.
                </p>
              </section>

              {(() => {
                const av = selected.availability;
                const upcoming = upcomingLeave(av, clockIso); // ends today-or-later, sorted ascending (soonest first)
                const upcomingIds = new Set(upcoming.map((b) => b.id));
                const past = av.leaveBlocks
                  .filter((b) => !upcomingIds.has(b.id))
                  .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()); // newest first
                const doubleDays = countDoubleDaysSinceLastDayOff(selected, Object.values(bookings), clockIso);
                const tally = leaveTallyByType(av.leaveBlocks);

                const row = (b: LeaveBlock) => {
                  const tone = dayOffTileTone(leaveBlockKind(b.type));
                  return (
                    <li key={b.id} className="flex items-start justify-between gap-3 rounded-lg border border-cream-faint/15 px-3 py-2">
                      <span className="min-w-0">
                        <span className={clsx('mr-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase', tone.fill, tone.text)}>{tone.label}</span>
                        <span className="text-sm text-cream">{formatLeaveRange(b)}</span>
                        {b.type === 'comp' && b.compensatesDate && (
                          <span className="block text-xs text-cream-faint">
                            Compensates double day on {format(new Date(`${b.compensatesDate}T00:00:00`), 'd MMM')}
                          </span>
                        )}
                        {b.note && <span className="block truncate text-xs text-cream-faint">{b.note}</span>}
                      </span>
                      <button type="button" aria-label="Remove day off" className="shrink-0 text-cream-faint hover:text-blood" onClick={() => onRemoveLeave(b.id)}>
                        <X size={14} />
                      </button>
                    </li>
                  );
                };

                return (
                  <section className="mt-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-cream">Days off</span>
                      <DoubleDayBadge count={doubleDays} />
                    </div>

                    <div
                      className="mt-3 flex flex-wrap items-center gap-2"
                      data-testid="leave-tally"
                    >
                      <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                        Leave taken
                      </span>
                      {LEAVE_TYPES.map((t) => (
                        <span
                          key={t}
                          data-testid={`leave-tally-${t}`}
                          className={clsx(
                            'inline-flex items-baseline gap-1.5 rounded-md border px-2 py-1 text-xs',
                            tally.byType[t] > 0
                              ? 'border-gold-dim/40 text-cream'
                              : 'border-cream-faint/15 text-cream-faint',
                          )}
                        >
                          {LEAVE_TYPE_LABEL[t]}
                          <b className="font-mono tabular-nums">{tally.byType[t]}</b>
                        </span>
                      ))}
                      <span className="inline-flex items-baseline gap-1.5 rounded-md border border-gold-bright/40 bg-gold-bright/5 px-2 py-1 text-xs text-gold-bright">
                        Total
                        <b className="font-mono tabular-nums" data-testid="leave-tally-total">
                          {tally.total}
                        </b>
                        <span className="text-cream-faint">days</span>
                      </span>
                    </div>

                    {/* L7: annual allowance + remaining. RDO + comp never draw it
                        down. Allowance is owner/georgina-editable in place. */}
                    {(() => {
                      // D10-02: working days this year, the same rule as the cap.
                      const consuming = allowanceUsedThisYear(av, clockIso);
                      const allowed = selected.leaveAllowanceDays ?? 0;
                      const remaining = allowed - consuming;
                      return (
                        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="leave-allowance">
                          <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                            Allowance
                          </span>
                          {allowAllStudios ? (
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={selected.leaveAllowanceDays ?? ''}
                              onChange={(e) => onUpdateAllowance(Number(e.target.value))}
                              className="w-16 bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1 text-sm text-cream font-mono"
                              data-testid="artist-leave-allowance"
                              aria-label="Annual leave allowance in days"
                            />
                          ) : (
                            <span className="font-mono text-sm text-cream" data-testid="artist-leave-allowance">
                              {allowed}
                            </span>
                          )}
                          <span className="text-cream-faint text-xs">
                            days · taken <b className="font-mono tabular-nums text-cream">{consuming}</b>
                          </span>
                          <span
                            className={clsx(
                              'inline-flex items-baseline gap-1.5 rounded-md border px-2 py-1 text-xs',
                              remaining < 0 ? 'border-blood/50 text-blood' : 'border-mint/40 text-mint',
                            )}
                            data-testid="artist-leave-remaining"
                          >
                            <b className="font-mono tabular-nums">{remaining}</b> left
                          </span>
                          <span className="text-cream-faint text-[11px]">RDO + comp don&apos;t count</span>
                        </div>
                      );
                    })()}

                    <div className="mt-3 flex flex-wrap items-end gap-2">
                      <label className="block text-[10px] font-inter uppercase tracking-widest text-cream-soft">
                        Start
                        <input
                          type="date"
                          value={leaveStart}
                          onChange={(e) => setLeaveStart(e.target.value)}
                          className="mt-1 block bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
                          data-testid="artist-leave-start"
                        />
                      </label>
                      <label className="block text-[10px] font-inter uppercase tracking-widest text-cream-soft">
                        End
                        <input
                          type="date"
                          value={leaveEnd}
                          min={leaveStart || undefined}
                          onChange={(e) => setLeaveEnd(e.target.value)}
                          className="mt-1 block bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
                          data-testid="artist-leave-end"
                        />
                      </label>
                      <label className="block text-[10px] font-inter uppercase tracking-widest text-cream-soft">
                        Type
                        <select
                          value={leaveType}
                          onChange={(e) => setLeaveType(e.target.value as LeaveBlock['type'])}
                          className="mt-1 block bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
                          data-testid="artist-leave-type"
                        >
                          <option value="time-off">time-off</option>
                          <option value="rdo">rdo</option>
                          <option value="comp">Comp day</option>
                          <option value="flex-swap">flex-swap</option>
                          <option value="annual">annual</option>
                        </select>
                      </label>
                      <label className="block text-[10px] font-inter uppercase tracking-widest text-cream-soft flex-1 min-w-[160px]">
                        Note
                        <input
                          type="text"
                          value={leaveNote}
                          onChange={(e) => setLeaveNote(e.target.value)}
                          placeholder="Holiday, training, family event"
                          className="mt-1 block w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
                          data-testid="artist-leave-note"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => void onAddLeave()}
                        disabled={leaveInvalid}
                        className="btn-primary text-xs"
                        data-testid="artist-add-leave"
                      >
                        <Plus size={12} /> Add leave
                      </button>
                    </div>
                    {leaveError ? (
                      <p role="alert" className="mt-2 text-sm text-blood" data-testid="artist-leave-error">
                        {leaveError}
                      </p>
                    ) : null}

                    <div className="mt-3">
                      <p className="text-xs uppercase tracking-wide text-cream-faint">Upcoming</p>
                      {upcoming.length === 0 ? (
                        <p className="mt-1 text-xs text-cream-faint">No upcoming days off.</p>
                      ) : (
                        <ul data-testid="leave-upcoming" className="mt-1 space-y-1.5">{upcoming.map(row)}</ul>
                      )}
                    </div>

                    {past.length > 0 && (
                      <div className="mt-3">
                        <button
                          type="button"
                          data-testid="leave-past-toggle"
                          className="flex items-center gap-1 text-xs text-cream-faint hover:text-cream"
                          onClick={() => setPastOpen((o) => !o)}
                        >
                          {pastOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          Past days off ({past.length})
                        </button>
                        {pastOpen && <ul data-testid="leave-past" className="mt-1 space-y-1.5">{past.map(row)}</ul>}
                      </div>
                    )}
                  </section>
                );
              })()}

              <section className="pt-2 border-t border-gold-dim/20">
                {selected.archived ? (
                  <div className="flex items-center justify-between">
                    <span className="font-inter text-[10px] text-blood uppercase tracking-widest">
                      Archived
                    </span>
                    <button
                      type="button"
                      onClick={onRestore}
                      className="btn-quiet text-xs"
                      data-testid="artist-restore"
                    >
                      Restore to roster
                    </button>
                  </div>
                ) : confirmingArchive ? (
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-serif text-sm text-cream-soft">
                      Archive {selected.displayName} from the roster?
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmingArchive(false)}
                        className="btn-quiet text-xs"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={onArchive}
                        className="btn-primary text-xs"
                        data-testid="artist-archive-confirm"
                      >
                        Archive
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingArchive(true)}
                    className="btn-quiet text-xs text-blood"
                    data-testid="artist-archive"
                  >
                    Remove from roster
                  </button>
                )}
              </section>
            </div>
          ) : (
            <div className="panel p-6 font-serif text-cream-soft text-center">
              Pick an artist on the left to edit their details, pattern, and leave.
            </div>
          )}
        </aside>
      </div>
      )}

      {showAdd ? (
        <AddArtistModal
          studios={studioOptions(studios)}
          defaultStudioId={isAllStudios(activeStudio) ? (activeStudioIds(studios, assignedStudios)[0] ?? studioOptions(studios)[0]!.id) : (activeStudio as StudioId)}
          onCancel={() => setShowAdd(false)}
          onCreate={onCreateArtist}
        />
      ) : null}
    </section>
  );
}

/**
 * L7: per-studio leave ledger. One row per artist — allowance allowed / taken /
 * remaining — plus a type breakdown where RDO + comp are shown but marked
 * non-consuming ("n/c"), and a studio-total footer. Studio scope follows the
 * screen's studio filter (the `groups` passed in).
 */
function LeaveLedgerView({
  groups,
  studioName,
  nowIso,
}: {
  groups: Array<[string, Artist[]]>;
  studioName: (id: StudioId) => string;
  nowIso: string;
}) {
  return (
    <div className="mt-6 max-w-7xl space-y-6" data-testid="leave-ledger">
      <p className="font-serif text-cream-soft text-sm max-w-2xl">
        Days allowed vs taken per artist. RDO and comp days are shown but never consume the
        allowance — RDO is workload balancing, and comp repays a double day already worked.
      </p>
      {groups.length === 0 ? (
        <p className="font-serif text-cream-soft" data-testid="leave-ledger-empty">
          No artists in this view.
        </p>
      ) : null}
      {groups.map(([studioId, artists]) => {
        const rows = artists.map((a) => {
          const blocks = a.availability.leaveBlocks;
          const allowed = a.leaveAllowanceDays ?? 0;
          // D10-02: working days this year, the same rule as the cap.
          const taken = allowanceUsedThisYear(a.availability, nowIso);
          return { a, allowed, taken, remaining: allowed - taken, byType: leaveTallyByType(blocks).byType };
        });
        const totAllowed = rows.reduce((s, r) => s + r.allowed, 0);
        const totTaken = rows.reduce((s, r) => s + r.taken, 0);
        return (
          <div
            key={studioId}
            className="panel p-0 overflow-x-auto"
            data-testid={`leave-ledger-studio-${studioId}`}
          >
            <p className="px-4 pt-4 pb-1 label-small-caps text-gold-bright">
              {studioName(studioId as StudioId)}
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-cream-faint font-inter text-[10px] uppercase tracking-widest border-b border-gold-dim/20">
                  <th className="px-4 py-2.5 font-normal">Artist</th>
                  <th className="px-3 py-2.5 font-normal text-right">Allowed</th>
                  <th className="px-3 py-2.5 font-normal text-right">Taken</th>
                  <th className="px-3 py-2.5 font-normal text-right">Left</th>
                  <th className="px-4 py-2.5 font-normal">Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.a.id}
                    className="border-t border-gold-dim/10"
                    data-testid={`leave-ledger-row-${r.a.id}`}
                  >
                    <td className="px-4 py-2 font-inter text-cream whitespace-nowrap">{r.a.displayName}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-cream">{r.allowed}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-cream">{r.taken}</td>
                    <td
                      className={clsx(
                        'px-3 py-2 text-right font-mono tabular-nums',
                        r.remaining < 0 ? 'text-blood' : 'text-mint',
                      )}
                      data-testid={`leave-ledger-remaining-${r.a.id}`}
                    >
                      {r.remaining}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1.5">
                        {LEAVE_TYPES.filter((t) => r.byType[t] > 0).map((t) => {
                          const nonConsuming = !ALLOWANCE_CONSUMING_TYPES.includes(t);
                          return (
                            <span
                              key={t}
                              className={clsx(
                                'inline-flex items-baseline gap-1 rounded-md border px-1.5 py-0.5 text-[11px]',
                                nonConsuming
                                  ? 'border-cream-faint/25 text-cream-faint'
                                  : 'border-gold-dim/40 text-cream',
                              )}
                            >
                              {LEAVE_TYPE_LABEL[t]} <b className="font-mono">{r.byType[t]}</b>
                              {nonConsuming ? (
                                <span className="text-[9px] uppercase tracking-wider">· n/c</span>
                              ) : null}
                            </span>
                          );
                        })}
                        {LEAVE_TYPES.every((t) => r.byType[t] === 0) ? (
                          <span className="text-cream-faint text-[11px]">No leave booked</span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gold-dim/40" data-testid={`leave-ledger-total-${studioId}`}>
                  <td className="px-4 py-2 font-inter text-cream font-semibold">Studio total</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-cream font-semibold">{totAllowed}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-cream font-semibold">{totTaken}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-cream font-semibold">
                    {totAllowed - totTaken}
                  </td>
                  <td className="px-4 py-2" />
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
